import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexFill,
  ApexTooltip,
  ApexXAxis,
  ApexLegend,
  ApexDataLabels,
  ApexTitleSubtitle,
  ApexPlotOptions,
  ApexYAxis,
  ApexGrid,
  ApexResponsive,
} from 'ng-apexcharts';
import { Subscription } from 'rxjs';
import { NavigationService } from 'libs/portal/src/services/navigation/navigation.service';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  xaxis: ApexXAxis;
  markers: any;
  stroke: any;
  yaxis: ApexYAxis | ApexYAxis[];
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  colors: string[];
  labels: string[] | number[];
  title: ApexTitleSubtitle;
  subtitle: ApexTitleSubtitle;
  legend: ApexLegend;
  fill: ApexFill;
  tooltip: ApexTooltip;
  grid?: ApexGrid;
};

export interface DeviceOnlineDateModel {
  Online: number;
  IntervalStart: string;
}

declare global {
  interface Window {
    Apex: any;
  }
}

@Component({
  selector: 'app-data-device-over-time-chart',
  templateUrl: './data-device-over-time-chart.component.html',
  styleUrls: ['./data-device-over-time-chart.component.scss'],
})
export class DataDeviceOverTimeChartComponent implements OnInit, OnDestroy {
  @Input() data: DeviceOnlineDateModel[];
  @ViewChild('chart') chart: ChartComponent;
  public chartOptions: Partial<ChartOptions>;
  public commonAreaSparlineOptions: Partial<ChartOptions> = {};
  @Output() timeFrame: EventEmitter<any> = new EventEmitter<any>();
  @Output() daterangeStart: EventEmitter<any> = new EventEmitter<any>();
  @Output() daterangeEnd: EventEmitter<any> = new EventEmitter<any>();
  @Output() monthlyToggleChanged = new EventEmitter();
  @Output() dailyToggleChanged = new EventEmitter();

  @Input() monthlyToggle;
  @Input() dailyToggle;
  @Input() defaultStart;
  @Input() defaultEnd;
  @Input() defaultTimeFrame;
  @Input() isMikrotik = false;
  @Input() isKeepAlive = false;
  @Input() isCDR = false;

  dateChanged = false;
  tempStart;
  tempEnd;
  selectedTimeframe = '3';

  graphLoaded = false;
  sidenavObservable: Subscription;

  constructor(private tools: ToolsService, private navigationService: NavigationService) {}

  public randomizeArray(arg): number[] {
    const array = arg.slice();
    let currentIndex = array.length,
      temporaryValue,
      randomIndex;

    while (0 !== currentIndex) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;

      temporaryValue = array[currentIndex];
      array[currentIndex] = array[randomIndex];
      array[randomIndex] = temporaryValue;
    }

    return array;
  }

  ngOnInit() {
    let series: any = [];
    let seriesName = 'Online Devices';
    if (this.isCDR) {
      seriesName = 'Online CDR Devices';
    } else if (this.isMikrotik) {
      seriesName = 'Online MikroTik Devices';
    } else if (this.isKeepAlive) {
      seriesName = 'Online Keep Alive Devices';
    }

    series = [
      {
        name: seriesName,
        data: this.data != null ? this.data.map((device) => device.Online) : [],
        color: '#4BB543',
      },
    ];

    let max: any = undefined;
    try {
      series.forEach((element) => {
        max = Math.max(...element.data) + 10;
      });
    } finally {
    }

    this.chartOptions = {
      series: series,
      title: {
        text: 'Devices Online Per Day',
        align: 'left',
        style: {
          fontSize: '24px',
          color: '#08074B',
          fontWeight: '400',
        },
      },
      chart: {
        id: 'chart1',
        type: 'line',
        height: 300,
        dropShadow: {
          enabled: true,
          color: '#000',
          top: 18,
          left: 7,
          blur: 10,
          opacity: 0.2,
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: 'bottom',
              offsetX: -10,
              offsetY: 0,
            },
          },
        },
      ],
      markers: {
        size: 0,
      },
      dataLabels: {
        enabled: true,
        textAnchor: 'middle',
        offsetX: 1,
      },
      stroke: {
        curve: 'smooth',
        width: 4,
      },
      grid: {
        yaxis: {
          lines: {
            show: false,
          },
        },
        xaxis: {
          lines: {
            show: true,
          },
        },
      },
      xaxis: {
        categories: this.data != null ? this.data.map((device) => this.tools.FormatSwedishDate(new Date(device.IntervalStart))) : [],
        axisBorder: {
          show: true,
        },
      },
      yaxis: {
        min: 0,
        max: max,
      },
      tooltip: {
        fixed: {
          enabled: true,
        },
        x: {
          show: true,
        },
        marker: {
          show: true,
        },
      },
    };

    this.graphLoaded = true;
    this.selectedTimeframe = this.defaultTimeFrame.toString();

    this.sidenavObservable = this.navigationService.GetSideNav(() => {
      if (this.graphLoaded != false) {
        const temp = this.graphLoaded;
        this.graphLoaded = false;
        setTimeout(() => {
          this.graphLoaded = temp;
        });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.sidenavObservable != null) {
      this.sidenavObservable.unsubscribe();
    }
  }

  timeFrameChange(event) {
    setTimeout(() => {
      this.selectedTimeframe = event.value;
    });

    this.timeFrame.emit(event.value);
  }

  monthlyToggleChangeFunc() {
    this.monthlyToggleChanged.emit();
  }

  dailyToggleChangeFunc() {
    this.dailyToggleChanged.emit();
  }

  startDateInput(event) {
    this.daterangeStart.emit(event.value);
  }

  endDateInput(event) {
    this.daterangeEnd.emit(event.value);
  }

  clearPicker() {
    this.tempStart = this.defaultStart;
    this.tempEnd = this.defaultEnd;
    this.defaultStart = null;
    this.defaultEnd = null;
  }

  dateChange(event) {
    if (!event) {
      this.defaultStart = this.tempStart;
      this.defaultEnd = this.tempEnd;
    }
  }
}
